import { useEffect } from 'react'
import { useBreadcrumb } from '../hooks/useBreadcrumb'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
// FaShip 已随 quickLinks 里的「散货拼柜」下线一并移出；恢复时需重新加回本行。
import { FaPlusCircle, FaSearch, FaFileAlt, FaClock, FaShippingFast, FaCheckCircle, FaWallet, FaHistory, FaExclamationTriangle, FaCalculator } from 'react-icons/fa'
import { useAuthStore } from '../store'
import { dashboardService } from '../services'
import { Card, StatusBadge, LoadingOverlay, PageHeader, MobileCard } from '../components/common'
import { AmountDisplay } from '../hooks'
import { formatDateTime } from '../utils/datetime'

const statCards = [
  { key: 'total', label: '总报名数', icon: FaFileAlt, color: 'bg-primary-500' },
  { key: 'pending', label: '处理中', icon: FaClock, color: 'bg-yellow-500' },
  { key: 'shipped', label: '运输中', icon: FaShippingFast, color: 'bg-cyan-500' },
  { key: 'delivered', label: '已完成', icon: FaCheckCircle, color: 'bg-green-500' },
]

const quickLinks = [
  { path: '/consolidation', label: '集运报名', icon: FaPlusCircle },
  // 散货板块已下线（2026-07-30），注释保留。详见 components/layout/Navbar.jsx 的说明。
  // { path: '/lcl', label: '散货拼柜', icon: FaShip },
  { path: '/issues', label: '问题反馈', icon: FaExclamationTriangle },
  { path: '/freight-calculator', label: '运费估算', icon: FaCalculator },
]

export default function DashboardPage() {
  const breadcrumb = useBreadcrumb()
  const { customer, isGuest, isAuthenticated, init } = useAuthStore()
  const isGuestMode = isGuest || !isAuthenticated

  useEffect(() => {
    init()
  }, [init])

  const { data: stats, isLoading: statsLoading } = useQuery({
    queryKey: ['customerStats'],
    queryFn: dashboardService.getCustomerStats,
    enabled: !isGuestMode, // 访客模式下不请求
  })

  const { data: summary, isLoading: summaryLoading } = useQuery({
    queryKey: ['clientSummary'],
    queryFn: dashboardService.getClientSummary,
    enabled: !isGuestMode, // 访客模式下不请求
  })

  if (!isGuestMode && (statsLoading || summaryLoading)) {
    return <LoadingOverlay message="加载中..." />
  }

  return (
    <div>
      <PageHeader
        title={isGuestMode ? '欢迎体验，访客用户' : `欢迎回来，${customer?.customer_name || '尊敬的客户'}`}
        subtitle={isGuestMode ? '登录后可查看完整数据和管理订单' : '管理您的物流订单，追踪货物状态'}
        breadcrumb={breadcrumb}
      />

      <div className="container py-8">
        {/* Quick Actions */}
        <div className="flex flex-wrap gap-3 mb-8">
          <Link
            to="/consolidation"
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors"
          >
            <FaPlusCircle />
            新建报名
          </Link>
          <Link
            to="/tracking"
            className="inline-flex items-center gap-2 px-5 py-2.5 border border-primary-600 text-primary-600 rounded-md hover:bg-primary-50 transition-colors"
          >
            <FaSearch />
            追踪货物
          </Link>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-fluid-xs gap-4 mb-8">
          {statCards.map((card) => {
            const Icon = card.icon
            return (
              <Card key={card.key} className="px-4 py-5 sm:p-5">
                {/* 两列时每格仅 136px，图标 48px + gap 后留给数字的不足 40px，
                    小屏改成图标在上、数字在下 */}
                <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
                  <div className={`w-11 h-11 sm:w-12 sm:h-12 shrink-0 rounded-lg ${card.color} flex items-center justify-center text-white`}>
                    <Icon className="text-xl" />
                  </div>
                  <div className="min-w-0">
                    <div className="text-fluid-2xl font-bold text-gray-900">
                      {stats?.[card.key] ?? '-'}
                    </div>
                    <div className="text-sm text-gray-500">{card.label}</div>
                  </div>
                </div>
              </Card>
            )
          })}
        </div>

        {/* Main Content Grid */}
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Account Balance */}
          <Card>
            <Card.Header>
              <h5 className="flex items-center gap-2 font-semibold">
                <FaWallet className="text-primary-500" />
                账户余额
              </h5>
            </Card.Header>
            <Card.Body>
              <div className="text-center py-4">
                <div className="text-fluid-3xl font-bold text-gray-900 mb-2">
                  <AmountDisplay amount={stats?.balance || 0} showLoginPrompt={isGuestMode} />
                </div>
                <Link
                  to="/finance"
                  className="text-sm text-primary-600 hover:text-primary-700"
                >
                  查看明细 →
                </Link>
              </div>
            </Card.Body>
          </Card>

          {/* Recent Bookings */}
          <div className="lg:col-span-2">
            <Card>
              <Card.Header>
                <div className="flex items-center justify-between">
                  <h5 className="flex items-center gap-2 font-semibold">
                    <FaHistory className="text-primary-500" />
                    最近报名
                  </h5>
                  <Link
                    to="/cargo"
                    className="text-sm text-primary-600 hover:text-primary-700"
                  >
                    查看全部
                  </Link>
                </div>
              </Card.Header>
              <Card.Body className="p-0">
                {/* 小屏卡片视图 */}
                <div className="md:hidden space-y-3 p-4">
                  {summary?.recent_bookings?.length > 0 ? (
                    summary.recent_bookings.map((booking) => (
                      <MobileCard
                        key={booking.name}
                        title={
                          <Link to={`/my-consolidation?code=${booking.name}`} className="text-primary-600 hover:underline">
                            {booking.name}
                          </Link>
                        }
                        badge={<StatusBadge status={booking.status} />}
                        fields={[
                          { label: '货物名称', value: booking.cargo_name },
                          { label: '创建时间', value: formatDateTime(booking.creation, 'YYYY-MM-DD') },
                        ]}
                      />
                    ))
                  ) : (
                    <div className="px-4 py-8 text-center text-gray-500">暂无报名记录</div>
                  )}
                </div>

                <div className="hidden md:block overflow-x-auto">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>报名单号</th>
                        <th>货物名称</th>
                        <th>状态</th>
                        <th>创建时间</th>
                      </tr>
                    </thead>
                    <tbody>
                      {summary?.recent_bookings?.length > 0 ? (
                        summary.recent_bookings.map((booking) => (
                          <tr key={booking.name}>
                            <td>
                              {/* 报名详情已并入「我的报名」页（不再有 /cargo/<单号> 这条路由）。
                                  这里只有单号、没有整条数据，所以带 ?code= 过去让那边定位到对应行。 */}
                              <Link
                                to={`/my-consolidation?code=${booking.name}`}
                                className="text-primary-600 hover:underline"
                              >
                                {booking.name}
                              </Link>
                            </td>
                            <td>{booking.cargo_name}</td>
                            <td><StatusBadge status={booking.status} /></td>
                            <td className="text-gray-500 text-sm">
                              {formatDateTime(booking.creation, 'YYYY-MM-DD')}
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={4} className="text-center py-8 text-gray-500">
                            暂无报名记录
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </Card.Body>
            </Card>
          </div>
        </div>

        {/* Quick Links */}
        <div className="mt-8">
          <h5 className="font-semibold text-gray-700 mb-4">快捷入口</h5>
          <div className="grid grid-cols-fluid-xs gap-4">
            {quickLinks.map((link) => {
              const Icon = link.icon
              return (
                <Link
                  key={link.path}
                  to={link.path}
                  className="flex flex-col items-center justify-center gap-3 p-6 bg-white rounded-lg border border-gray-200 hover:border-primary-300 hover:shadow-md transition-all"
                >
                  <Icon className="text-2xl text-primary-500" />
                  <span className="font-medium text-gray-700">{link.label}</span>
                </Link>
              )
            })}
          </div>
        </div>
      </div>
    </div>
  )
}
